<!--
  Attachment Grid Viewer Component Template
  =======================================
  
  This template displays uploaded attachments in a visual grid layout with separate
  sections for media (images/videos) and files. It provides a comprehensive overview
  of all form attachments with click-to-view functionality.
  
  Core Features:
  - Media grid with image/video thumbnails
  - File list with type icons and metadata
  - Attachment count and requirements display
  - Delete functionality for individual and bulk operations
  - Modal integration with attachment viewer
  - Responsive design for different screen sizes
  
  User Interaction:
  - Click media/file to open in full-screen viewer
  - Delete buttons for individual attachments
  - Bulk delete all attachments option
  - Visual feedback for attachment requirements
  
  @author MacSafety Development Team
  @version 1.0
-->
<!-- Count indicator -->
<ion-item lines="none" *ngIf="minAttachments > 0">
  <ion-label [color]="attachments.length < minAttachments ? 'danger' : 'medium'">
    <p>
      {{ attachments.length }} attachments added
      <span *ngIf="attachments.length < minAttachments"> ({{ minAttachments }} recommended)</span>
    </p>
  </ion-label>
</ion-item>

<!-- Media Section (Photos & Videos) -->
<div *ngIf="getMediaAttachments().length > 0">
  <ion-item-divider>
    <ion-label>Media</ion-label>
  </ion-item-divider>
  
  <ion-grid class="ion-no-padding">
    <ion-row>
      <ion-col 
        size="auto"
        *ngFor="let attachment of getMediaAttachments()"
        class="media-col"
      >
        <div class="media-wrapper">
          <ion-card class="media-card" (click)="onOpenAttachmentModal(getAttachmentIndex(attachment))">
            <div class="media-thumbnail">
              <!-- For images, show the image -->
              <img 
                *ngIf="!isVideo(attachment)"
                [src]="attachment.url" 
                [alt]="attachment.name"
                class="thumbnail-image"
              />
              
              <!-- For videos, show video thumbnail using video element -->
              <div *ngIf="isVideo(attachment)" class="video-thumbnail">
                <video 
                  [src]="attachment.url"
                  class="thumbnail-image"
                  preload="metadata"
                  muted
                  #videoElement
                ></video>
                
                <!-- Video play overlay -->
                <div class="video-overlay">
                  <ion-icon name="play-circle" color="light"></ion-icon>
                </div>
              </div>
            </div>
          </ion-card>
          
          <!-- Delete button outside the card -->
          <ion-button 
            *ngIf="editable"
            fill="solid"
            color="danger"
            size="small"
            class="delete-button-external"
            (click)="onDeleteAttachment(attachment.id)"
          >
            <ion-icon name="close" size="small"></ion-icon>
          </ion-button>
      
        </div>
      </ion-col>
    </ion-row>
  </ion-grid>
</div>

<!-- Files Section -->
<div *ngIf="getFileAttachments().length > 0">
  <ion-item-divider>
    <ion-label>Files</ion-label>
  </ion-item-divider>
  
  <ion-list>
    <ion-item 
      button
      *ngFor="let attachment of getFileAttachments()"
      (click)="onOpenAttachmentModal(getAttachmentIndex(attachment))"
    >
      <ion-icon 
        [name]="getFileIcon(attachment)" 
        slot="start"
        color="medium"
      ></ion-icon>
      
      <ion-label>
        <h3>{{ truncateFilename(attachment.name) }}</h3>
        <p>{{ formatFileSize(attachment.size) }}</p>
      </ion-label>
      
      <ion-button 
        *ngIf="editable"
        fill="clear" 
        color="danger" 
        slot="end"
        (click)="onDeleteAttachment(attachment.id); $event.stopPropagation()"
      >
        <ion-icon name="trash-outline" slot="icon-only"></ion-icon>
      </ion-button>
    </ion-item>
  </ion-list>
</div>

<!-- Empty state -->
<ion-item *ngIf="attachments.length === 0" lines="none">
  <ion-icon name="attach-outline" slot="start" color="medium" size="large"></ion-icon>
  <ion-label color="medium">
    <p>No attachments added</p>
  </ion-label>
</ion-item>

<!-- Delete All button -->
<ion-button 
  *ngIf="attachments.length > 0 && editable"
  expand="block" 
  color="danger" 
  fill="outline"
  class="ion-margin"
  (click)="onDeleteAllAttachments()"
>
  <ion-icon name="trash-outline" slot="start"></ion-icon>
  Delete All Attachments
</ion-button>

<!-- Attachment Viewer Modal -->
<ion-modal 
  [isOpen]="isAttachmentModalOpen" 
  (didDismiss)="closeAttachmentModal()" 
  [backdropDismiss]="true" 
  [cssClass]="'fullscreen-modal ignore-dark-mode'" 
  [backdropOpacity]="0.9"
>
<ng-template>
  <!-- Floating Close Button -->
    <ion-fab vertical="top" horizontal="end" slot="fixed">
      <ion-fab-button 
        size="small" 
        color="light" 
        (click)="closeAttachmentModal()"
        class="modal-close-fab"
      >
        <ion-icon name="close"></ion-icon>
      </ion-fab-button>
    </ion-fab>

  <app-attachment-viewer-modal
    [isOpen]="isAttachmentModalOpen"
    [identifier]="identifier"
    [initialIndex]="selectedAttachmentIndex"
    (closeModal)="closeAttachmentModal()"
  ></app-attachment-viewer-modal>
</ng-template>
</ion-modal>